/**
 * Signing in.
 *
 * The first screen of a shift, often on a tablet, often by someone in a hurry.
 * Two decisions follow from that:
 *
 * The form submits properly rather than through a click handler, so Enter
 * works and a password manager can fill it.
 *
 * Failures say what to do next. "Invalid credentials" is the server being
 * careful about not revealing which half was wrong; the branch machine being
 * off is a different problem with a different fix, and the two must not read
 * the same.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../stores/auth.js';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';
import logo from '../../assets/logo.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useAuth((s) => s.signIn);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);

    try {
      await signIn(email.trim(), password);
      // Back to wherever they were headed before being asked to sign in.
      navigate(location.state?.from ?? '/', { replace: true });
    } catch (error) {
      setProblem(
        error.offline
          ? 'Cannot reach the branch computer. Check it is switched on and connected.'
          : error.message
      );
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-2 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src={logo} alt="Food Run" className="mx-auto h-11 w-auto" width="512" height="97" />
          <p className="mt-4 text-sm text-ink-2">Sign in to start your shift</p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl border border-rule bg-surface p-6">
          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            required
            // The first thing anyone does here is type, so save them a tap.
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@foodrun.pk"
          />

          <Field
            className="mt-4"
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {problem && (
            <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
              {problem}
            </p>
          )}

          <Button type="submit" size="lg" busy={busy} className="mt-6 w-full">
            {busy ? 'Signing in' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  );
}
